import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

const S = {
  font: "'Sora', system-ui, sans-serif",
  heading: "'Fraunces', Georgia, serif",
};

export default function StudentExamOrdersPage() {
  const { i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  const { data: orders, isLoading } = useQuery({
    queryKey: ['student-exam-orders'],
    queryFn: async () => {
      const res = await fetch('/api/student/exam-orders', { credentials: 'include' });
      if (!res.ok) throw new Error('Failed');
      return res.json();
    },
  });

  const statusLabel = (s: string) => {
    const map: Record<string, { label: string; labelAr: string; bg: string; color: string }> = {
      paid: { label: 'Paid', labelAr: 'مدفوع', bg: '#dcfce7', color: '#16a34a' },
      pending: { label: 'Pending', labelAr: 'قيد الانتظار', bg: '#fef9c3', color: '#ca8a04' },
      failed: { label: 'Failed', labelAr: 'فشل', bg: '#fee2e2', color: '#dc2626' },
      refunded: { label: 'Refunded', labelAr: 'مسترد', bg: '#f3f4f6', color: '#6b7280' },
    };
    return map[s] || map.pending;
  };

  return (
    <div style={{ padding: '32px 24px' }}>
      <h1 style={{ fontFamily: S.heading, fontSize: 28, fontWeight: 600, color: 'var(--soleil-ink)', marginBottom: 8 }}>
        {isRTL ? 'طلبات الامتحانات' : 'My Exam Orders'}
      </h1>
      <p style={{ fontFamily: S.font, fontSize: 14, color: 'var(--soleil-mut)', marginBottom: 32 }}>
        {isRTL ? 'عرض حالة طلبات الامتحانات الخاصة بك' : 'View your exam purchase history and status'}
      </p>

      {isLoading ? (
        <div style={{ textAlign: 'center', padding: 48, fontFamily: S.font, color: 'var(--soleil-mut)' }}>Loading...</div>
      ) : !orders?.length ? (
        <div style={{ textAlign: 'center', padding: 48 }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>📋</div>
          <p style={{ fontFamily: S.font, fontSize: 15, color: 'var(--soleil-mut)', marginBottom: 20 }}>
            {isRTL ? 'لم تشترِ أي امتحان بعد' : "You haven't purchased any exams yet"}
          </p>
          <Link to="/exams" style={{
            fontFamily: S.font, fontSize: 14, fontWeight: 700, color: '#fff',
            background: '#b8860b', padding: '10px 24px', borderRadius: 50,
            textDecoration: 'none', display: 'inline-block',
          }}>
            {isRTL ? 'تصفح الامتحانات' : 'Browse Exams'}
          </Link>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {orders.map((order: any) => {
            const st = statusLabel(order.status);
            return (
              <div key={order.id} style={{ background: '#fff', borderRadius: 16, border: '1px solid var(--soleil-sand)', padding: '20px 24px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
                <div>
                  <h3 style={{ fontFamily: S.heading, fontSize: 18, fontWeight: 600, color: 'var(--soleil-ink)', marginBottom: 6 }}>
                    {isRTL ? order.examTitleAr : order.examTitle}
                  </h3>
                  <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                    <span style={{ fontFamily: S.font, fontSize: 12, padding: '3px 10px', borderRadius: 20, background: order.examType === 'computer' ? '#dbeafe' : '#fef3c7', color: order.examType === 'computer' ? '#2563eb' : '#d97706' }}>
                      {order.examType === 'computer' ? (isRTL ? '💻 إلكتروني' : '💻 Computer') : (isRTL ? '📝 ورقي' : '📝 Paper')}
                    </span>
                    <span style={{ fontFamily: S.font, fontSize: 12, color: 'var(--soleil-mut)' }}>
                      {order.createdAt ? new Date(order.createdAt).toLocaleDateString() : ''}
                    </span>
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontFamily: S.heading, fontSize: 20, fontWeight: 700, color: 'var(--soleil-ink)', marginBottom: 6 }}>
                    {order.amount?.toLocaleString()} <span style={{ fontSize: 12, fontWeight: 400, color: 'var(--soleil-mut)' }}>{order.currency}</span>
                  </div>
                  <span style={{ fontFamily: S.font, fontSize: 12, fontWeight: 600, padding: '3px 12px', borderRadius: 20, background: st.bg, color: st.color }}>
                    {isRTL ? st.labelAr : st.label}
                  </span>
                </div>
              </div>
              {/* Exam code & admin status for paid orders */}
              {order.status === 'paid' && (
                <div style={{ borderTop: '1px solid var(--soleil-sand)', marginTop: 16, paddingTop: 14, display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
                  <div style={{ fontFamily: S.font, fontSize: 13, color: 'var(--soleil-sub)' }}>
                    {isRTL ? 'حالة الطلب:' : 'Request Status:'}{' '}
                    <strong style={{ color: order.adminStatus === 'approved' ? '#16a34a' : order.adminStatus === 'rejected' ? '#dc2626' : '#ca8a04' }}>
                      {order.adminStatus === 'approved' ? (isRTL ? 'تمت الموافقة' : 'Approved')
                        : order.adminStatus === 'rejected' ? (isRTL ? 'مرفوض' : 'Rejected')
                        : order.adminStatus === 'completed' ? (isRTL ? 'مكتمل' : 'Completed')
                        : (isRTL ? 'قيد المراجعة' : 'Under Review')}
                    </strong>
                  </div>
                  {order.examCode && (
                    <div style={{ fontFamily: S.font, fontSize: 13 }}>
                      <span style={{ color: 'var(--soleil-sub)' }}>{isRTL ? 'رمز الامتحان:' : 'Exam Code:'} </span>
                      <span style={{ fontFamily: 'monospace', background: 'var(--soleil-gold-pale)', padding: '2px 10px', borderRadius: 6, fontWeight: 700, color: 'var(--soleil-ink)' }}>{order.examCode}</span>
                    </div>
                  )}
                  {order.adminNotes && (
                    <div style={{ fontFamily: S.font, fontSize: 12, color: 'var(--soleil-sub)', fontStyle: 'italic' }}>
                      {order.adminNotes}
                    </div>
                  )}
                </div>
              )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
